import { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Condition, RuleMetric, RuleOp } from '@/types/tags';

// A flat representation of a rule for the UI: each slot is one leaf condition
// with a bracket (AND or OR). The group operator applies to the whole list.
// This is deliberately simpler than the full AST the backend supports — v1
// only needs single-level groups with leaves, which covers every seeded
// segment and every realistic teacher-authored rule.
export interface BuilderState {
  group: 'all' | 'any';
  leaves: Leaf[];
}

export interface Leaf {
  metric: RuleMetric;
  op: RuleOp;
  value: string;
}

const metricOptions: { value: RuleMetric; label: string; type: 'number' | 'boolean' | 'string' }[] = [
  { value: 'days_since_signup', label: 'Days since signup', type: 'number' },
  { value: 'days_since_last_session', label: 'Days since last session', type: 'number' },
  { value: 'sessions_completed_total', label: 'Sessions completed (total)', type: 'number' },
  { value: 'sessions_completed_7d', label: 'Sessions completed (7d)', type: 'number' },
  { value: 'sessions_completed_30d', label: 'Sessions completed (30d)', type: 'number' },
  { value: 'current_streak', label: 'Current streak', type: 'number' },
  { value: 'longest_streak', label: 'Longest streak', type: 'number' },
  { value: 'completion_rate_30d', label: 'Completion rate (30d)', type: 'number' },
  { value: 'assignments_pending', label: 'Assignments pending', type: 'number' },
  { value: 'assignments_overdue', label: 'Assignments overdue', type: 'number' },
  { value: 'assignments_completed_30d', label: 'Assignments completed (30d)', type: 'number' },
  { value: 'reports_pending', label: 'Reports pending', type: 'number' },
  { value: 'has_user_problem', label: 'Has an active problem', type: 'boolean' },
  { value: 'has_active_subscription', label: 'Has active subscription', type: 'boolean' },
  { value: 'has_tag_slug', label: 'Has tag (slug)', type: 'string' },
];

const numericOps: { value: RuleOp; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '≠' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
];
const booleanOps: { value: RuleOp; label: string }[] = [
  { value: 'eq', label: 'is' },
  { value: 'ne', label: 'is not' },
];
const stringOps: { value: RuleOp; label: string }[] = [
  { value: 'eq', label: 'is' },
  { value: 'ne', label: 'is not' },
  { value: 'contains', label: 'contains' },
];

function opsForMetric(metric: RuleMetric): { value: RuleOp; label: string }[] {
  const meta = metricOptions.find((m) => m.value === metric);
  if (!meta) return numericOps;
  if (meta.type === 'boolean') return booleanOps;
  if (meta.type === 'string') return stringOps;
  return numericOps;
}

function metricType(metric: RuleMetric): 'number' | 'boolean' | 'string' {
  return metricOptions.find((m) => m.value === metric)?.type ?? 'number';
}

// stateToCondition builds the backend Condition JSON from the flat UI state.
export function stateToCondition(state: BuilderState): Condition | null {
  const valid = state.leaves.filter((l) => l.metric && l.op && l.value !== '');
  if (valid.length === 0) return null;
  const leafNodes: Condition[] = valid.map((l) => ({
    metric: l.metric,
    op: l.op,
    value: coerceValue(metricType(l.metric), l.value),
  }));
  if (leafNodes.length === 1) return leafNodes[0];
  return state.group === 'any' ? { any: leafNodes } : { all: leafNodes };
}

function coerceValue(type: 'number' | 'boolean' | 'string', raw: string): unknown {
  if (type === 'number') return Number(raw);
  if (type === 'boolean') return raw === 'true';
  return raw;
}

// conditionToState inverts stateToCondition for editing existing rules.
// Only handles the single-level shape produced by stateToCondition; anything
// more complex renders as a single editable leaf and keeps the rest in the
// stored rule object when saved.
export function conditionToState(rule: Condition | null | undefined): BuilderState {
  if (!rule) return { group: 'all', leaves: [] };
  if (rule.all || rule.any) {
    const list = rule.all ?? rule.any ?? [];
    return {
      group: rule.any ? 'any' : 'all',
      leaves: list.map(leafFromCondition),
    };
  }
  return { group: 'all', leaves: [leafFromCondition(rule)] };
}

function leafFromCondition(c: Condition): Leaf {
  return {
    metric: (c.metric ?? 'days_since_signup') as RuleMetric,
    op: (c.op ?? 'gte') as RuleOp,
    value: c.value == null ? '' : String(c.value),
  };
}

interface RuleBuilderProps {
  state: BuilderState;
  onChange: (next: BuilderState) => void;
}

export function RuleBuilder({ state, onChange }: RuleBuilderProps) {
  const { group, leaves } = state;

  const update = (idx: number, patch: Partial<Leaf>) => {
    const next = leaves.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    onChange({ group, leaves: next });
  };
  const remove = (idx: number) => {
    onChange({ group, leaves: leaves.filter((_, i) => i !== idx) });
  };
  const add = () => {
    onChange({
      group,
      leaves: [...leaves, { metric: 'days_since_signup', op: 'gte', value: '' }],
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Match</span>
        <Select
          value={group}
          onValueChange={(v) => onChange({ group: (v ?? 'all') as 'all' | 'any', leaves })}
        >
          <SelectTrigger className="h-8 w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">ALL of</SelectItem>
            <SelectItem value="any">ANY of</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">the following rules:</span>
      </div>

      <div className="space-y-2">
        {leaves.map((leaf, idx) => (
          <LeafRow
            key={idx}
            leaf={leaf}
            onChange={(patch) => update(idx, patch)}
            onRemove={() => remove(idx)}
          />
        ))}
      </div>

      <Button variant="outline" size="sm" onClick={add}>
        <Plus className="mr-1 h-3.5 w-3.5" />
        Add rule
      </Button>
    </div>
  );
}

function LeafRow({
  leaf,
  onChange,
  onRemove,
}: {
  leaf: Leaf;
  onChange: (patch: Partial<Leaf>) => void;
  onRemove: () => void;
}) {
  const ops = useMemo(() => opsForMetric(leaf.metric), [leaf.metric]);
  const type = metricType(leaf.metric);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={leaf.metric}
        onValueChange={(v) => {
          const m = (v ?? 'days_since_signup') as RuleMetric;
          onChange({ metric: m, op: opsForMetric(m)[0].value });
        }}
      >
        <SelectTrigger className="h-8 w-60">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {metricOptions.map((m) => (
            <SelectItem key={m.value} value={m.value}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={leaf.op} onValueChange={(v) => onChange({ op: (v ?? 'eq') as RuleOp })}>
        <SelectTrigger className="h-8 w-24">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ops.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {type === 'boolean' ? (
        <Select value={leaf.value} onValueChange={(v) => onChange({ value: v ?? '' })}>
          <SelectTrigger className="h-8 w-28">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">true</SelectItem>
            <SelectItem value="false">false</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <Input
          value={leaf.value}
          onChange={(e) => onChange({ value: e.target.value })}
          placeholder={type === 'number' ? '0' : 'slug'}
          className="h-8 w-40"
          type={type === 'number' ? 'number' : 'text'}
        />
      )}

      <Button variant="ghost" size="icon" onClick={onRemove}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
